/**
 * VoxyManager — module-level singleton that owns the VoxyCore instance and
 * bridges it to EventRuntime, SentenceController, and (via the parlaySTT
 * event-graph node) downstream STT.
 *
 * VoxyManager handles audio capture only. Transcription is a graph decision —
 * wire onSpeechEnd → parlaySTT to produce a transcript, then feed that to
 * sendTextInput. This module no longer knows about any STT provider.
 *
 * PCM buffer: voxy's `utterance-audio` event delivers the full utterance as a
 * WAV-wrapped buffer on speech-end. We strip the 44-byte WAV header to get
 * raw little-endian 16-bit PCM at 16 kHz mono — the format parlay expects —
 * and retain the latest completed utterance so parlaySTT can pick it up
 * without passing binary data through the graph.
 */
import { VoxyCore } from '@avataros/voxy'
import { EventRuntime } from './EventRuntime'
import { sentenceController } from './SentenceController'
import { devLog } from './devLog'

let _voxy = null
let _generation = 0
let _handlers = null
let _active = false
let _lastError = null  // { code: 'mic-unavailable'|'start-failed', message } | null
const _activeSubs = new Set()  // callbacks for onActiveChange

function _notifySubs() {
  for (const cb of _activeSubs) {
    try { cb(_active) } catch (e) { console.warn('[VoxyManager] active-sub threw:', e) }
  }
}

function _setActive(v) {
  if (_active === v) return
  _active = v
  _notifySubs()
}

function _setError(err) {
  _lastError = err
  // Error transitions must reach subscribers even when `_active` doesn't
  // change (failed start: false → false). The UI shows a visible error
  // state instead of silently rendering nothing.
  _notifySubs()
}

// ── Echo guard ────────────────────────────────────────────────────────────
// While the avatar speaks, its own TTS comes back through the user's
// speakers and the mic. getUserMedia's echoCancellation does NOT reliably
// cancel WebAudio playback, so with the normal VAD thresholds the avatar's
// voice trips speech detection in the AVATAR_SPEAKING state → VoxyCore emits
// `barge-in` → sentenceController.reset() kills the response mid-sentence
// (live-confirmed: barge-in fired 100ms after TTS onset with nobody
// speaking — the "mouth stops moving mid-conversation" bug).
//
// Mitigation: while AVATAR_SPEAKING, require a much more deliberate signal
// before accepting a barge-in — higher VAD probability, longer sustained
// speech, and a stronger energy gate (close-mic user speech scores far
// above speaker echo on all three). The user-configured values are restored
// the moment the avatar goes idle. This is deliberately NOT configurable
// per-blueprint yet; the real long-term fix is acoustic echo cancellation
// with the TTS stream as the reference signal, which belongs in the voxy
// package.
const ECHO_GUARD_VAD = Object.freeze({
  vadThreshold: 0.9,
  minSpeechMs: 500,
  energyGateMult: 4.0,
})
let _echoGuardSaved = null  // pre-guard values of the ECHO_GUARD_VAD fields, or null when inactive

// Latest completed utterance, keyed by sequenceId. Only the most recent is
// retained — there's no reason to remember earlier ones once STT has run.
let _latestPCM = null  // { sequenceId, pcm: ArrayBuffer }

// Synchronously captured on 'utterance-audio', consumed on 'speech-end'.
// Held as-is (Blob or ArrayBuffer) so the capture is non-async and can't
// race with the speech-end handler that reads it.
let _pendingUtterance = null  // { sequenceId, audio: Blob|ArrayBuffer }

const WAV_HEADER_BYTES = 44

export const voxyManager = {
  /**
   * Construct the VoxyCore instance. Build-time fields (sampleRate, modelUrl)
   * are fixed; runtime thresholds supplied by `config` are forwarded to the
   * constructor. Idempotent — second call is a no-op.
   */
  init(config = {}) {
    if (_voxy) return
    _voxy = new VoxyCore({
      sampleRate: 16000,
      modelUrl: '/models/silero_vad.onnx',
      ...config,
    })
    this._wireEvents()
    this._wireSentenceController()
  },

  async start({ muted = false } = {}) {
    if (!_voxy) this.init()
    const gen = _generation
    try {
      await _voxy.start()
    } catch (err) {
      // No microphone (CI container, denied permission, no input device): the
      // rest of the app — graph runtimes, sentence playback, text-input path —
      // is independent of audio capture and must keep working. Swallow the
      // rejection and log a single tagged line. Bubbling this would crash the
      // boot path that VoxyCore is woken up from.
      const msg = err?.message || String(err)
      const isMicError = err?.name === 'MicError'
        || err?.code === 'MIC_UNAVAILABLE'
        || /microphone|getUserMedia|NotFoundError|NotAllowedError/i.test(msg)
      if (isMicError) {
        console.warn('[VoxyManager] MIC_UNAVAILABLE — continuing without audio capture:', msg)
      } else {
        console.error('[VoxyManager] VoxyCore.start() failed:', err)
      }
      _setActive(false)
      _setError({ code: isMicError ? 'mic-unavailable' : 'start-failed', message: msg })
      return
    }
    if (gen !== _generation) return
    if (!_voxy.isStarted()) {
      console.warn('[VoxyManager] start() resolved but VoxyCore failed — check errors above')
      _setError({ code: 'start-failed', message: 'VoxyCore did not reach started state' })
      return
    }
    if (muted) _voxy.mute()
    _lastError = null
    _setActive(true)
    devLog('[VoxyManager] started — state:', _voxy.getState(), muted ? '(muted)' : '')
  },

  stop() {
    _generation++
    if (_voxy) {
      if (_handlers) {
        for (const [event, fn] of _handlers) _voxy.off(event, fn)
        _handlers = null
      }
      _voxy.stop()
      _voxy = null
    }
    _latestPCM = null
    _echoGuardSaved = null
    sentenceController.onAvatarSpeakingChange = null
    _setActive(false)
    // Deliberate teardown is not a failure — clear any stale error so the
    // UI doesn't keep showing a mic-error pill after e.g. a character switch.
    // _setError (not a bare assignment) so subscribers hear about it even
    // when `_active` was already false (a failed start followed by stop()).
    _setError(null)
    devLog('[VoxyManager] stopped')
  },

  getVoxy() { return _voxy },

  /** Last start failure ({ code, message }) or null. Cleared on successful start and on stop(). */
  getLastError() { return _lastError },

  mute() { _voxy?.mute() },
  unmute() { _voxy?.unmute() },

  /** True once VoxyCore has successfully started and is capturing audio. */
  isActive() { return _active },

  /**
   * Subscribe to active-state changes. Callback fires on every transition.
   * Returns an unsubscribe function. Does not fire on initial subscribe —
   * caller should read `isActive()` if it needs the current value.
   */
  onActiveChange(cb) {
    _activeSubs.add(cb)
    return () => _activeSubs.delete(cb)
  },

  /**
   * Apply a partial runtime-tunable VAD config to the running VoxyCore.
   * Safe to call any time; VoxyCore propagates it to AudioPipeline
   * immediately. No-op if voxy isn't running yet.
   *
   * While the echo guard is active (avatar speaking), the guarded fields are
   * folded into the saved base config instead of the live one, so a blueprint
   * live-update can't lower the barge-in bar mid-response; they apply as soon
   * as the avatar goes idle.
   */
  updateVadConfig(partial) {
    if (!_voxy) return
    if (_echoGuardSaved) {
      const { live, deferred } = _splitGuardedFields(partial)
      Object.assign(_echoGuardSaved, deferred)
      if (Object.keys(live).length) _voxy.updateVadConfig(live)
      return
    }
    _voxy.updateVadConfig(partial)
  },

  /** Pre-guard VAD values while the echo guard is active, else null. Test/debug hook. */
  getEchoGuardSaved() { return _echoGuardSaved ? { ..._echoGuardSaved } : null },

  /**
   * Latest completed utterance as raw PCM (16kHz mono 16-bit LE), or null if
   * no utterance has landed yet. Does not clear the buffer — the parlaySTT
   * node reads it during execution after onSpeechEnd fires.
   */
  getLatestPCM() {
    return _latestPCM ? _latestPCM.pcm : null
  },

  /** Latest utterance's sequenceId, or null. */
  getLatestSequenceId() {
    return _latestPCM ? _latestPCM.sequenceId : null
  },

  // ── Internal wiring ───────────────────────────────────────────────────────

  _wireEvents() {
    const on = (event, fn) => {
      _voxy.on(event, fn)
      return [event, fn]
    }

    _handlers = [
      on('speech-start', (p) => {
        devLog('[VoxyManager] speech-start seq=%d', p.sequenceId)
        EventRuntime.fireEvent('onSpeechStart', { sequenceId: p.sequenceId })
      }),

      on('utterance-audio', (p) => {
        // Capture synchronously — the Blob→ArrayBuffer conversion runs in
        // the speech-end handler below so onSpeechEnd doesn't fire before
        // _latestPCM is populated. Voxy emits utterance-audio immediately
        // before speech-end (in the same synchronous call path), so any
        // await here creates a race with the graph-side parlaySTT node.
        _pendingUtterance = { sequenceId: p.sequenceId, audio: p.audio }
      }),

      on('speech-end', async (p) => {
        // Finish the PCM conversion before firing onSpeechEnd so any node
        // wired to it (parlaySTT) sees the up-to-date _latestPCM.
        // Voxy wraps the utterance in WAV; Parlay expects raw PCM, so we
        // slice the 44-byte WAV header off here.
        if (_pendingUtterance && _pendingUtterance.sequenceId === p.sequenceId) {
          const audio = _pendingUtterance.audio
          _pendingUtterance = null
          const arrayBuffer = audio instanceof Blob ? await audio.arrayBuffer() : audio
          const pcm = arrayBuffer.byteLength > WAV_HEADER_BYTES
            ? arrayBuffer.slice(WAV_HEADER_BYTES)
            : arrayBuffer
          _latestPCM = { sequenceId: p.sequenceId, pcm }
          devLog('[VoxyManager] utterance-audio seq=%d pcm=%d bytes (%dms)',
            p.sequenceId, pcm.byteLength, p.durationMs)
        }
        devLog('[VoxyManager] speech-end seq=%d dur=%dms', p.sequenceId, p.durationMs)
        EventRuntime.fireEvent('onSpeechEnd', { durationMs: p.durationMs, sequenceId: p.sequenceId })
      }),

      on('barge-in', (p) => {
        devLog('[VoxyManager] barge-in seq=%d', p.sequenceId)
        sentenceController.reset()
        EventRuntime.fireEvent('onBargeIn', { sequenceId: p.sequenceId })
      }),

      on('text-submit', (p) => {
        devLog('[VoxyManager] text-submit: "%s"', p.text)
        EventRuntime.fireEvent('onTextInput', { text: p.text })
      }),

      on('error', (p) => console.error('[Voxy] error %s: %s', p.code, p.message)),
      on('warn', (p) => console.warn('[Voxy] warn %s: %s', p.code, p.message)),

      on('state-change', (p) => {
        devLog('[VoxyManager] state %s → %s', p.from, p.to)
      }),
    ]
  },

  _wireSentenceController() {
    sentenceController.onAvatarSpeakingChange = (speaking) => {
      if (!_voxy) return
      if (speaking) {
        // Apply the echo guard BEFORE entering AVATAR_SPEAKING so no VAD
        // frame is evaluated at the permissive thresholds while the state
        // machine would treat a trigger as a barge-in. Idempotent across the
        // per-sentence (true) calls of a multi-sentence response — only the
        // first one snapshots the base values.
        if (!_echoGuardSaved) {
          _echoGuardSaved = _pickGuardedFields(_voxy.config)
          _voxy.updateVadConfig(ECHO_GUARD_VAD)
        }
        _voxy.avatarSpeaking()
      } else {
        if (_echoGuardSaved) {
          _voxy.updateVadConfig(_echoGuardSaved)
          _echoGuardSaved = null
        }
        _voxy.avatarIdle()
      }
    }
  },
}

// The VAD fields the echo guard overrides, snapshotted from VoxyCore's live
// config (the single source of truth for defaults + blueprint overrides).
function _pickGuardedFields(config) {
  const out = {}
  for (const k of Object.keys(ECHO_GUARD_VAD)) out[k] = config[k]
  return out
}

// Split a partial VAD update into guard-overridden fields (deferred until the
// avatar goes idle) and everything else (applied live).
function _splitGuardedFields(partial) {
  const live = {}, deferred = {}
  for (const [k, v] of Object.entries(partial)) {
    if (k in ECHO_GUARD_VAD) deferred[k] = v
    else live[k] = v
  }
  return { live, deferred }
}

// Test/debug access (tests/e2e drive start-failure paths through this).
if (typeof window !== 'undefined') window.__VOXY_MANAGER__ = voxyManager
